import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "reactstrap";
import { testTransactionID } from "../example/exampleTransaction";
import { captureException } from "@sentry/react";
import axiosWithRetry from "../axios";
import LanguageSelector from "../components/LanguageSelector";
import ErrorHandler from "../components/ErrorHandler";
import PaymentConfFailed from "../components/PaymentConfirmation/PaymentConfFailed";
import PaymentConfInitial from "../components/PaymentConfirmation/PaymentConfInitial";
import PaymentConfSuccess from "../components/PaymentConfirmation/PaymentConfSuccess";
import PaymentConfStarted from "../components/PaymentConfirmation/PaymentConfStarted";
import PaymentConfCanceled from "../components/PaymentConfirmation/PaymentConfCanceled";
import CallWait from "../components/CallWait";
import Footer from "../components/Footer";
import {
  API_CALL_TIMEOUT,
  API_GET_PAYMENT_STATUS,
  DEFAULT_LANGUAGE,
  FEATURE_INTENT_RETRY,
  LANGUAGES,
} from "../Constants";

import RedpayLogo from "../assets/images/logos/redpay-small.png";
import { llog } from "../utils/LogUtils";

const ConfirmationPage = () => {
  const { t, i18n } = useTranslation();
  const { transactionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const languageParam = searchParams.get("lang");
  const langFound = LANGUAGES.find((lang) => lang.lang === languageParam);
  const language = langFound ? languageParam : DEFAULT_LANGUAGE;

  const setLanguage = (lang) => {
    searchParams.set("lang", lang);
    setSearchParams(searchParams);
  };

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [transactionStatus, setTransactionStatus] = useState(null);

  const status = transactionStatus?.status;

  const redirect2Confirm = () => {
    window.location.href = transactionStatus.merchantConfirmUrl;
  };

  const redirect2Failed = () => {
    if (transactionStatus.merchantFailedUrl === "") {
      window.location.href = transactionStatus.merchantConfirmUrl + "?error=true";
    } else {
      window.location.href = transactionStatus.merchantFailedUrl;
    }
  };

  const cancelPayment = () => {
    // If PI can be retried then we need to cancel the transaction
    if (FEATURE_INTENT_RETRY) {
      const axios = axiosWithRetry();
      const abortController = new AbortController();
      llog("Canceling transaction");
      setLoading(true);
      const signal = abortController.signal;
      axios
        .post(`${API_GET_PAYMENT_STATUS}/${transactionId}/cancel`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          setTransactionStatus(response.data);
          llog(response.data);
          setLoading(false);
          redirect2Failed();
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setError(error);
            llog(error);
            captureException(error);
            setLoading(false);
          }
        });
    } else {
      redirect2Failed();
    }
  };

  useEffect(() => {
    i18n.changeLanguage(language);

    // Provide easy way to test the transaction in development
    if (process.env.NODE_ENV === "development" && transactionId === testTransactionID) {
      setTransactionStatus(transactionStatus);
      setLoading(false);
      return;
    }

    const axios = axiosWithRetry();
    const abortController = new AbortController();
    if (!transactionStatus) {
      setLoading(true);
      const signal = abortController.signal;
      axios
        .get(`${API_GET_PAYMENT_STATUS}/${transactionId}/status`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          setTransactionStatus(response.data);
          llog(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setError(error);
            llog(error);
            captureException(error);
            setLoading(false);
          }
        });
    }
    return () => {
      abortController.abort();
    };
  }, [transactionId, transactionStatus, i18n, language]);

  if (error) return <ErrorHandler message={"Error Code:" + error.status} description={t("error_500")} />;

  if (loading) return <CallWait message={t("payname.blik")} description={t("call_wait_desc")} />;

  return (
    <div className='app-wrapper bg-white min-vh-100'>
      <Container>
        <Row className='my-5 align-items-center'>
          <Col md='10' lg='8'>
            <div>
              <img src={RedpayLogo} alt='Redpay logo' className='mx-2 mb-2' width={200} />
              <strong>{t("welcome")}</strong>
            </div>
          </Col>
          <Col md='2' lg='4' className='text-right'>
            <LanguageSelector language={language} setLanguage={setLanguage} />
          </Col>
        </Row>
        <Row className='my-5' style={{ marginBottom: "auto" }}>
          <Col className='text-center'>
            {status === "INITIAL" && <PaymentConfInitial lang={language} trans={transactionId} />}
            {status === "SUCCESS" && <PaymentConfSuccess handleContinue={redirect2Confirm} />}
            {status === "FAILED" && (
              <PaymentConfFailed lang={language} trans={transactionId} handleCancel={cancelPayment} />
            )}
            {status === "STARTED" && <PaymentConfStarted lang={language} trans={transactionId} />}
            {status === "CANCELED_USER" && <PaymentConfCanceled handleContinue={redirect2Failed} />}
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
};

export default ConfirmationPage;
